import { create } from 'zustand';
import { getComponentStore } from '../../../utils/componentStoreRegistry';
import { generateDefaultColumnName } from './generateDefaultColumnName';

const createDatabasesStore = () => create((set, get) => ({
  loaded: false,
  data: null,
  type: 'databases',

  databaseColumn: (existingFields = []) => ({
    columnId: (Math.floor(Math.random() * 90000000) + 10000).toString(),
    columnName: generateDefaultColumnName(
      existingFields.map((f) => f.columnName).filter(Boolean)
    ),
    columnType: 'TEXT',
    errorMessage: '',
  }),

  componentKeyData: () => {
    const column = get().databaseColumn();
    return {
      id: (Math.floor(Math.random() * 90000000) + 10000).toString(),
      dbName: '',
      dbType: 'text',
      configureDB: 'new_empty_database',
      fields: [column],
    };
  },

  setDatabasePage: database => ({
    id: database.databaseId,
    dbName: database.name || database.dbName,
    dbImage: database.image,
    updatedAt: database.updatedAt,
    workspaceId: database.workspaceId || null,
    storageFormat: database.storageFormat || 'EAV',
    columns: database.columns || [],
  }),

  setDatabase: (value, type) => {
    set(s => {
      const data = { ...s.data, createNewDatabase: { ...s.data.createNewDatabase } };
      switch (type) {
        case 'dbName':
          data.createNewDatabase.dbName = value;
          break;
        case 'configureDB':
          data.createNewDatabase.configureDB = value;
          break;
      }
      return { data };
    });
  },

  clearDatabase: () => {
    set(s => ({
      data: { ...s.data, databases: [], databaseSample: [] },
    }));
  },

  searchDatabase: database => {
    set(s => ({ data: { ...s.data, databases: database } }));
  },

  addDatabase: database => {
    const obj = get().setDatabasePage(database);
    set(s => ({
      data: {
        ...s.data,
        databases: [...s.data.databases, obj],
        createNewDatabase: get().componentKeyData(),
      },
    }));
  },

  addDatabaseSample: databaseSample => {
    const obj = get().setDatabasePage(databaseSample);
    set(s => ({
      data: {
        ...s.data,
        databaseSample: [...s.data.databaseSample, obj],
        createNewDatabase: get().componentKeyData(),
      },
    }));
  },

  addComponentKeyData: () => {
    set((s) => {
      const fields = s.data.createNewDatabase.fields;
      const obj = get().databaseColumn(fields);
      return {
        data: {
          ...s.data,
          createNewDatabase: {
            ...s.data.createNewDatabase,
            fields: [...fields, obj],
          },
        },
      };
    });
  },

  deleteComponentKeyData: item => {
    set(s => {
      const fields = [...s.data.createNewDatabase.fields];
      const index = fields.findIndex(x => x.columnId === item.columnId);
      if (index >= 0) fields.splice(index, 1);
      return {
        data: {
          ...s.data,
          createNewDatabase: { ...s.data.createNewDatabase, fields },
        },
      };
    });
  },

  addNewDatabase: () => {
    set(s => ({
      data: {
        ...s.data,
        databases: [...s.data.databases, s.data.createNewDatabase],
        createNewDatabase: get().componentKeyData(),
      },
    }));
  },

  setCancel: () => {
    set(s => ({
      data: { ...s.data, createNewDatabase: get().componentKeyData() },
    }));
  },

  setField: (value, index, type) => {
    set(s => {
      const fields = [...s.data.createNewDatabase.fields];
      fields[index] = { ...fields[index] };
      switch (type) {
        case 'columnName':
          fields[index].columnName = value;
          break;
        case 'columnType':
          fields[index].columnType = value;
          break;
        case 'errorMessage':
          fields[index].errorMessage = value;
          break;
      }
      return {
        data: {
          ...s.data,
          createNewDatabase: { ...s.data.createNewDatabase, fields },
        },
      };
    });
  },

  initData: () => {
    const obj = get().componentKeyData();
    return {
      databaseSample: [],
      databases: [],
      createNewDatabase: obj,
    };
  },

  initialiseEmpty: () => {
    set({ loaded: false, data: get().initData(), type: 'databases' });
  },

  getTemplate: () => {
    const s = get();
    return { data: s.data, type: s.type };
  },
}));

export function getDatabasesStore(componentId) {
  return getComponentStore('databases', componentId, createDatabasesStore);
}

const useDatabasesStore = createDatabasesStore();

// Initialize on creation
useDatabasesStore.getState().initialiseEmpty();

export default useDatabasesStore;
